// TextureSlotField — PlayCanvas-style texture slot for a material's *Map
// fields. Three responsibilities:
//
//   1. Shows a thumbnail of the currently-bound texture (or an empty
//      checkerboard placeholder) plus the texture's name and a clear button.
//   2. Click → opens a popup listing every texture_asset for this character;
//      pick one to bind, or "None" to clear.
//   3. Drop target → drag an asset card with kind="texture" onto the field
//      to bind it. Drag-over highlights the slot.
//
// `value` is the texture asset UUID (or null/undefined for unbound).
// `onChange(uuid|null)` is fired on every change; the parent debounces the
// PATCH.

import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { assetRegistry } from '../../lib/assetRegistry'
import AnchoredOverlay from '../AnchoredOverlay'
import { Z } from '../../lib/zLayers'

const C = {
  bg:       '#080a10',
  bgRow:    '#11131b',
  border:   '#1f2129',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  accent:   '#8b6cff',
  drop:     '#4caf82',
}

export default function TextureSlotField({ label, value, texById, onChange, onNavigateToAsset }) {
  const [hover, setHover] = useState(false)
  const [open, setOpen] = useState(false)
  const fieldRef = useRef(null)

  const tex = value ? texById?.get(value) : null
  const previewUrl = tex?.file_url ?? null
  const texName = tex?.name ?? null
  const lastSelected = assetRegistry.getLastSelected('texture')
  const useSelected = () => { if (lastSelected) onChange(lastSelected) }

  // Drag-drop handlers. The drag source is the AssetCard in AssetGrid,
  // which sets dataTransfer with the asset id ("texture:<uuid>").
  const onDragOver = (e) => {
    if (!e.dataTransfer.types.includes('text/aos-asset-id')) return
    e.preventDefault()
    // 'copy' matches AssetCard's effectAllowed='copyMove'. 'link' was used
    // here before and shows the not-allowed cursor on Chromium/Firefox
    // because it's outside the source's effectAllowed set, which silently
    // suppresses the drop event even though dragover called preventDefault.
    e.dataTransfer.dropEffect = 'copy'
    setHover(true)
  }
  const onDragLeave = () => setHover(false)
  const onDrop = (e) => {
    setHover(false)
    const id = e.dataTransfer.getData('text/aos-asset-id')
    if (!id || !id.startsWith('texture:')) return
    const rawId = id.slice('texture:'.length)
    // legacy slot textures have a "." in their id and aren't real UUIDs.
    if (rawId.includes('.')) return
    e.preventDefault()
    onChange(rawId)
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        fontFamily: 'var(--m-font)', fontSize: 9, letterSpacing: '.1em',
        textTransform: 'uppercase', color: C.dim, marginBottom: 4,
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <div
          ref={fieldRef}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => setOpen(o => !o)}
          style={{
            flex: 1, minWidth: 0,
            display: 'flex', alignItems: 'center', gap: 8,
            background: C.bg,
            border: `1px solid ${hover ? C.drop : C.border}`,
            borderRadius: 3, padding: 4,
            cursor: 'pointer',
          }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); setOpen(o => !o) }}
            style={{
              width: 36, height: 36, borderRadius: 2, flexShrink: 0,
              background: previewUrl
                ? `url(${previewUrl}) center / cover`
                : `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 8px 8px`,
              border: `1px solid ${C.border}`,
              cursor: 'pointer',
            }}
            title="Click to pick a texture"
          />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{
              fontFamily: 'var(--m-font)', fontSize: 10, color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {texName ?? <span style={{ color: C.dim, fontStyle: 'italic' }}>none</span>}
            </div>
            {tex && (
              <div style={{ fontFamily: 'var(--m-mono)', fontSize: 9, color: C.dim }}>
                {tex.width && tex.height ? `${tex.width}×${tex.height}` : tex.format ?? ''}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); useSelected() }}
          disabled={!lastSelected}
          title={lastSelected ? 'Use last-selected texture in browser' : 'Select a texture in the content browser first'}
          style={slotIconBtn(!lastSelected)}
        >←</button>
        <button
          onClick={(e) => { e.stopPropagation(); if (value && onNavigateToAsset) onNavigateToAsset(`texture:${value}`) }}
          disabled={!value || !onNavigateToAsset}
          title="Find in content browser"
          style={slotIconBtn(!value || !onNavigateToAsset)}
        >🔍</button>
        {value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(null) }}
            title="Clear"
            style={slotIconBtn(false)}
          >✕</button>
        )}
      </div>
      {open && (
        <TexturePicker
          anchorRef={fieldRef}
          textures={CustomAssetManager.listTextureAssets()}
          current={value}
          onPick={(id) => { onChange(id); setOpen(false) }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  )
}

export function TexturePicker({ anchorRef, textures, current, onPick, onClose }) {
  const [filter, setFilter] = useState('')
  const popupRef = useRef(null)
  useEffect(() => {
    const onDocClick = (e) => {
      // Outside-click closes; clicks inside the anchor (its toggle) or the
      // popup itself (a row, the filter input) must NOT trigger close, or
      // mousedown would unmount the popup before the click reaches the row.
      if (anchorRef.current?.contains(e.target)) return
      if (popupRef.current?.contains(e.target)) return
      onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    // CAPTURE phase, and that is not a detail. This picker opens over the
    // material graph, whose ReactFlow pane is driven by d3-zoom — and d3-zoom
    // calls stopImmediatePropagation() on the pane's mousedown to claim the
    // drag. A bubble-phase document listener therefore NEVER sees a click on
    // the canvas: measured, a mousedown on .react-flow__pane reached document
    // exactly 0 times, so clicking away from the picker could not dismiss it.
    // Capture runs document -> target, so nothing downstream can swallow it.
    // Clicks inside the anchor or the popup are already excluded above.
    document.addEventListener('mousedown', onDocClick, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [anchorRef, onClose])

  const filtered = textures.filter(t => !filter || t.name.toLowerCase().includes(filter.toLowerCase()))

  // Position popup just below the anchor field.
  //
  // rect is in VIEWPORT pixels while a `position: fixed` left is multiplied by
  // the element's own CSS zoom, so writing rect.left straight in double-scaled
  // the offset. That conversion has to use THIS popup's zoom, not the anchor's,
  // and here the two genuinely differ: the portal below puts the popup under
  // <body> (the root UI-scale zoom) while the anchor can be inside a ReactFlow
  // node, where styles/index.css deliberately undoes that zoom to net 1.
  // AnchoredOverlay asks the element it positions, so it is right either way.
  //
  // The width uses offsetWidth rather than rect.width — a layout metric in the
  // element's own units (measured: 200, against rect's 220 at zoom 1.1) — so
  // the popup matches the field instead of overhanging it by the zoom factor.
  const rect = anchorRef.current?.getBoundingClientRect()
  const anchorW = anchorRef.current?.offsetWidth ?? 0

  // Portal to <body>: the popup is `position: fixed`, but when this field is
  // rendered inside a CSS-`transform`ed ancestor (e.g. a ReactFlow node in the
  // material graph), `fixed` resolves against that ancestor instead of the
  // viewport and the popup lands off-screen. Portaling escapes the transform.
  return createPortal(
    <AnchoredOverlay
      x={rect?.left ?? 0} y={(rect?.bottom ?? 0) + 4}
      innerRef={popupRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: Math.max(220, anchorW),
        // Above the surface that opened it — this picker is portaled to
        // <body>, and the material graph editor it can open over is a
        // fullscreen z-6500 takeover. See lib/zLayers.js.
        zIndex: Z.POPOVER,
        background: '#1a1d28', border: `1px solid ${C.border}`,
        borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,.45)',
        maxHeight: 320, overflowY: 'auto',
        padding: 4,
      }}
    >
      <input
        autoFocus
        placeholder="Filter…"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={{
          width: '100%', boxSizing: 'border-box',
          background: C.bg, border: `1px solid ${C.border}`,
          borderRadius: 3, padding: '4px 6px',
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 10,
          outline: 'none', marginBottom: 4,
        }}
      />
      <PickerRow current={!current} onClick={() => onPick(null)}>
        <div style={{
          width: 32, height: 32, borderRadius: 2,
          background: `repeating-conic-gradient(#1d1f28 0 25%, #14161e 0 50%) 50% / 8px 8px`,
        }} />
        <span style={{ color: C.dim, fontStyle: 'italic' }}>None</span>
      </PickerRow>
      {filtered.map(t => (
        <PickerRow key={t.id} current={t.id === current} onClick={() => onPick(t.id)}>
          <Thumb url={t.file_url} />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{
              fontFamily: 'var(--m-font)', fontSize: 10, color: C.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{t.name}</div>
            <div style={{ fontFamily: 'var(--m-mono)', fontSize: 9, color: C.dim }}>
              {t.width && t.height ? `${t.width}×${t.height}` : t.format ?? ''}
            </div>
          </div>
        </PickerRow>
      ))}
      {filtered.length === 0 && (
        <div style={{
          padding: 8, fontFamily: 'var(--m-font)', fontSize: 10,
          color: C.dim, textAlign: 'center',
        }}>
          No textures match.
        </div>
      )}
    </AnchoredOverlay>,
    document.body
  )
}

// One picker thumbnail.
//
// Three things, and each fixes something reported. It was a CSS `background:
// url(...)` before, which meant: the browser fetched the FULL-SIZE texture for
// a 32px square, fetched it for every row whether or not that row was scrolled
// into view, and gave no signal at all while it was in flight. On a catalogue
// with many textures that is the whole reason opening the list "takes some
// time" — the popup itself mounts instantly.
//
//   - <img loading="lazy"> so only rows the user actually scrolls to fetch;
//   - decoding="async" so a large image cannot block the row's paint;
//   - a shimmer skeleton until it lands, then a short cross-fade, so the row
//     reads as pending rather than broken.
//
// onError settles the same as onLoad: a texture whose file is missing must not
// shimmer forever, promising something that is never coming.
function Thumb({ url, size = 32 }) {
  const [settled, setSettled] = useState(false)
  return (
    <div
      className={settled ? undefined : 'aos-thumb-loading'}
      style={{
        position: 'relative', width: size, height: size, flexShrink: 0,
        borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.border}`,
        backgroundColor: '#14161e',
      }}
    >
      <img
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onLoad={() => setSettled(true)}
        onError={() => setSettled(true)}
        style={{
          width: '100%', height: '100%', objectFit: 'cover', display: 'block',
          opacity: settled ? 1 : 0, transition: 'opacity 140ms ease-out',
        }}
      />
    </div>
  )
}

function PickerRow({ current, onClick, children }) {
  const [hover, setHover] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        width: '100%', textAlign: 'left',
        background: current ? '#252938' : (hover ? '#1f2233' : 'transparent'),
        border: 'none', padding: 4, borderRadius: 3,
        cursor: 'pointer', color: C.text,
      }}
    >{children}</button>
  )
}

function slotIconBtn(disabled) {
  return {
    background: 'transparent',
    border: `1px solid ${disabled ? '#1f2129' : '#2a2c36'}`,
    color: disabled ? '#3a3c45' : C.dim,
    fontFamily: 'var(--m-font)', fontSize: 9,
    width: 22, height: 22, borderRadius: 3,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, padding: 0,
  }
}
